// src/components/Header.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/images/logo.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faBars,
  faX,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
} from "@fortawesome/free-brands-svg-icons";
import AnimatedButton from "./AnimatedButton";
import ContactPopup from "./ContactPopup";

function Header() {
  const [loading, setLoading] = useState(true);
  const [animatingToPosition, setAnimatingToPosition] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [fullscreenSearchOpen, setFullscreenSearchOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const servicesList = [
    { name: "Corporate Housekeeping", slug: "corporate-housekeeping" },
    { name: "Industrial Housekeeping", slug: "industrial-housekeeping" },
    { name: "Facade Cleaning", slug: "facade-cleaning" },
    { name: "Project Cleaning", slug: "project-cleaning" },
    { name: "Staffing Services", slug: "staffing-services" },
    { name: "Overseas maid Services", slug: "overseas-maid-services" },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatingToPosition(true);
      setTimeout(() => setLoading(false), 1500);
    }, 5000);

    // Handle ESC key for fullscreen search
    const handleEscKey = (e) => {
      if (e.key === "Escape" && fullscreenSearchOpen) {
        setFullscreenSearchOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscKey);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [fullscreenSearchOpen]);

  const handleMouseEnter = () => {
    setShowDropdown(true);
  };

  const handleMouseLeave = () => {
    setShowDropdown(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleMobileServices = () => {
    setMobileServicesOpen(!mobileServicesOpen);
  };

  const toggleFullscreenSearch = () => {
    setFullscreenSearchOpen(!fullscreenSearchOpen);
  };

  if (loading) {
    return (
      <>
        {/* Loading Screen */}
        <div className="loading-screen position-fixed w-100 h-100 d-flex justify-content-center align-items-center bg-white z-3">
          <div
            className={`logo-animation ${
              animatingToPosition ? "moving-to-header" : ""
            }`}
          >
            <img
              src={logo}
              alt="Logo"
              className="rounded-circle"
              style={{
                width: "auto",
                height: "130px",
                animation: animatingToPosition ? "none" : "pulse 1s infinite",
                transition: animatingToPosition
                  ? "all 1.5s cubic-bezier(0.4, 0, 0.2, 1)"
                  : "none",
                transform: animatingToPosition
                  ? "translate(0, -35vh) scale(0.8)"
                  : "none",
              }}
            />
          </div>
        </div>

        {/* Header during animation */}
        {animatingToPosition && (
          <header
            className="py-3 mt-4 fixed-top"
            style={{
              opacity: 0,
              animation: "fadeIn 1s ease-in-out 0.5s forwards",
            }}
          >
            <div className="container position-relative">
              <div className="header-box border rounded-5 d-flex justify-content-between align-items-center">
                {/* Left Nav */}
                <ul
                  className="nav d-none d-md-flex"
                  style={{
                    opacity: 0,
                    animation: "slideInLeft 1s ease-out 1s forwards",
                  }}
                >
                  <li className="nav-item">
                    <Link className="nav-link text-dark" to="/">
                      Home
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link text-dark" to="/about">
                      About
                    </Link>
                  </li>
                  {/* Dropdown with hover */}
                  <li
                    className="nav-item dropdown position-relative"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Link
                      className="nav-link dropdown-toggle text-dark"
                      to="/services"
                      role="button"
                      data-bs-toggle="dropdown"
                      aria-expanded={showDropdown}
                    >
                      Services
                    </Link>
                    <ul
                      className={`dropdown-menu ${showDropdown ? "show" : ""}`}
                      style={{
                        transition: "opacity 0.3s ease, transform 0.3s ease",
                        opacity: showDropdown ? 1 : 0,
                        transform: showDropdown
                          ? "translateY(0)"
                          : "translateY(-10px)",
                        pointerEvents: showDropdown ? "auto" : "none",
                      }}
                    >
                      {servicesList.map((srv) => (
                        <li key={srv.slug}>
                          <Link
                            className="dropdown-item"
                            to={`/services/${srv.slug}`}
                          >
                            {srv.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                  {/* <li className="nav-item">
                    <Link className="nav-link text-dark" to="/blogs">
                      Blogs
                    </Link>
                  </li> */}
                  <li className="nav-item">
                    <Link className="nav-link text-dark" to="/contact">
                      Contact
                    </Link>
                  </li>
                </ul>

                {/* Center Logo */}
                <div className="navbar-brand position-absolute start-50 translate-middle-x">
                  <div className="bg-white rounded-circle"></div>
                </div>

                {/* Right Icons */}
                <div
                  className="d-none d-md-flex align-items-center gap-3"
                  style={{
                    opacity: 0,
                    animation: "slideInRight 1s ease-out 1s forwards",
                  }}
                >
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control rounded-pill px-4 py-2"
                      placeholder="Search..."
                    />
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="position-absolute end-0 top-50 translate-middle-y me-2 text-muted"
                    />
                  </div>
                  <button
                    onClick={() => setShowPopup(true)}
                    className="border-0 bg-transparent"
                  >
                    <AnimatedButton text="Get in Touch" />
                  </button>
                </div>

                {/* Mobile Toggle */}
                <button
                  className="btn d-md-none"
                  type="button"
                  onClick={toggleMobileMenu}
                >
                  <FontAwesomeIcon icon={mobileMenuOpen ? faX : faBars} />
                </button>
              </div>
            </div>
          </header>
        )}
      </>
    );
  }

  return (
    <header
      className="py-3 mt-4 fixed-top"
      style={{ animation: "slideDown 0.4s ease-out" }}
    >
      <div className="container position-relative">
        <div className="header-box border rounded-5 d-flex justify-content-between align-items-center">
          {/* Left Nav */}
          <ul className="nav d-none d-md-flex">
            <li className="nav-item">
              <Link className="nav-link text-dark" to="/">
                Home
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link text-dark" to="/about">
                About
              </Link>
            </li>
            {/* Dropdown with hover */}
            <li
              className="nav-item dropdown position-relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                className="nav-link dropdown-toggle text-dark"
                to="/services"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded={showDropdown}
              >
                Services
              </Link>
              <ul
                className={`dropdown-menu ${showDropdown ? "show" : ""}`}
                style={{
                  transition: "opacity 0.3s ease, transform 0.3s ease",
                  opacity: showDropdown ? 1 : 0,
                  transform: showDropdown
                    ? "translateY(0)"
                    : "translateY(-10px)",
                  pointerEvents: showDropdown ? "auto" : "none",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  border: "1px solid rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px",
                  marginTop: "8px",
                }}
              >
                {servicesList.map((srv) => (
                  <li key={srv.slug}>
                    <Link
                      className="dropdown-item"
                      to={`/services/${srv.slug}`}
                      style={{
                        transition: "background-color 0.2s ease",
                        borderRadius: "6px",
                        listStyle: "none",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#f8f9fa";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "transparent";
                      }}
                    >
                      {srv.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
            {/* <li className="nav-item">
              <Link className="nav-link text-dark" to="/blogs">
                Blogs
              </Link>
            </li> */}
            <li className="nav-item">
              <Link className="nav-link text-dark" to="/contact">
                Contact
              </Link>
            </li>
          </ul>

          {/* Mobile Left Side - Hamburger Menu */}
          <div className="d-md-none">
            <button
              className="btn p-2"
              type="button"
              onClick={toggleMobileMenu}
              style={{
                backgroundColor: mobileMenuOpen ? "#e9ecef" : "#f8f9fa",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease",
                transform: mobileMenuOpen ? "rotate(90deg)" : "rotate(0deg)",
              }}
            >
              <FontAwesomeIcon
                icon={mobileMenuOpen ? faX : faBars}
                className="text-muted"
                size="sm"
              />
            </button>
          </div>

          {/* Center Logo */}
          <Link
            to="/"
            className="navbar-brand position-absolute start-50 translate-middle-x logo-final-position"
          >
            <div className="bg-white rounded-circle">
              <img
                src={logo}
                alt="Logo"
                className="rounded-circle"
                style={{
                  width: "auto",
                  height: "150px",
                  animation: "logoSettle 0.4s ease-out",
                }}
              />
            </div>
          </Link>

          {/* Right Icons - Desktop */}
          <div className="d-none d-md-flex align-items-center gap-3">
            <div className="position-relative">
              <input
                type="text"
                className="form-control rounded-pill px-4 py-2"
                placeholder="Search..."
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="position-absolute end-0 top-50 translate-middle-y me-2 text-muted"
              />
            </div>
            <button
              onClick={() => setShowPopup(true)}
              className="border-0 bg-transparent"
            >
              <AnimatedButton text="Get in Touch" />
            </button>
          </div>
          <ContactPopup
            show={showPopup}
            handleClose={() => setShowPopup(false)}
          />

          {/* Mobile Right Side - Search Icon */}
          <div className="d-md-none">
            <button
              className="btn p-2"
              type="button"
              onClick={toggleFullscreenSearch}
              style={{
                backgroundColor: fullscreenSearchOpen ? "#e9ecef" : "#f8f9fa",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                transform: fullscreenSearchOpen
                  ? "rotate(45deg)"
                  : "rotate(0deg)",
              }}
            >
              <FontAwesomeIcon
                icon={fullscreenSearchOpen ? faX : faSearch}
                className="text-muted"
                size="sm"
              />
            </button>
          </div>
        </div>

        {/* Fullscreen Search Overlay */}
        <div
          className={`position-fixed w-100 h-100 top-0 start-0 d-md-none ${fullscreenSearchOpen ? "d-flex" : "d-none"}`}
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            zIndex: 9999,
            backdropFilter: "blur(10px)",
            animation: fullscreenSearchOpen
              ? "fadeIn 0.4s ease-out"
              : "fadeOut 0.4s ease-out",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
          }}
        >
          {/* Search Header */}
          <div className="w-100 text-center mb-5">
            <h2 className="fw-bold text-dark mb-2" style={{ fontSize: "28px" }}>
              What are you looking for?
            </h2>
            <p className="text-muted">Search our services and content</p>
          </div>

          {/* Search Input */}
          <div className="w-100" style={{ maxWidth: "500px" }}>
            <div className="position-relative mb-4">
              <input
                type="text"
                className="form-control py-4 px-5 pe-6"
                placeholder="Type your search here..."
                autoFocus
                style={{
                  fontSize: "18px",
                  borderRadius: "50px",
                  border: "2px solid #e9ecef",
                  backgroundColor: "#fff",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#667eea";
                  e.target.style.boxShadow =
                    "0 15px 50px rgba(102, 126, 234, 0.2)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e9ecef";
                  e.target.style.boxShadow = "0 10px 40px rgba(0, 0, 0, 0.1)";
                }}
              />
              <button
                className="btn position-absolute end-0 top-50 translate-middle-y me-3"
                style={{
                  backgroundColor: "#667eea",
                  borderRadius: "50%",
                  width: "50px",
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#5a67d8";
                  e.target.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#667eea";
                  e.target.style.transform = "scale(1)";
                }}
              >
                <FontAwesomeIcon icon={faSearch} className="text-white" />
              </button>
            </div>
          </div>

          {/* Quick Search Suggestions */}
          <div className="w-100 text-center" style={{ maxWidth: "500px" }}>
            <p className="text-muted mb-3 small">Popular searches:</p>
            <div className="d-flex flex-wrap gap-2 justify-content-center">
              {servicesList.map((service, index) => (
                <button
                  key={service.slug}
                  className="btn btn-sm rounded-pill px-3 py-2"
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                    color: "#6c757d",
                    fontSize: "14px",
                    transition: "all 0.2s ease",
                    listStyle: "none",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#667eea";
                    e.target.style.color = "white";
                    e.target.style.borderColor = "#667eea";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#f8f9fa";
                    e.target.style.color = "#6c757d";
                    e.target.style.borderColor = "#e9ecef";
                  }}
                  onClick={() => {
                    // Handle search for this service
                    console.log(`Searching for: ${service.name}`);
                    toggleFullscreenSearch();
                  }}
                >
                  {service.name}
                </button>
              ))}
            </div>
          </div>

          {/* Close hint */}
          <div className="position-absolute bottom-0 start-0 w-100 text-center pb-4">
            <p className="text-muted small">
              Press ESC or click the X button to close
            </p>
          </div>
        </div>

        {/* Modern Mobile Menu Overlay */}
        <div
          className={`position-fixed w-100 h-100 top-0 start-0 d-md-none ${mobileMenuOpen ? "d-block" : "d-none"}`}
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9998,
            backdropFilter: "blur(8px)",
            animation: mobileMenuOpen
              ? "fadeIn 0.3s ease-out"
              : "fadeOut 0.3s ease-out",
          }}
          onClick={toggleMobileMenu}
        ></div>

        {/* Mobile Menu Drawer */}
        <div
          className={`position-fixed top-0 end-0 h-100 d-md-none ${mobileMenuOpen ? "" : ""}`}
          style={{
            width: "85%",
            maxWidth: "350px",
            backgroundColor: "#ffffff",
            zIndex: 9999,
            transform: mobileMenuOpen ? "translateX(0)" : "translateX(100%)",
            transition: "transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            boxShadow: "-10px 0 30px rgba(0, 0, 0, 0.15)",
            overflowY: "auto",
          }}
        >
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between p-4 border-bottom border-light">
            <div className="d-flex align-items-center gap-3">
              <img
                src={logo}
                alt="Logo"
                className="rounded-circle"
                style={{ width: "40px", height: "40px" }}
              />
              <h6 className="mb-0 fw-bold text-dark">Menu</h6>
            </div>
            <button
              className="btn btn-sm p-2"
              onClick={toggleMobileMenu}
              style={{
                backgroundColor: "#f8f9fa",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                transition: "all 0.2s ease",
              }}
            >
              <FontAwesomeIcon icon={faX} className="text-muted" size="sm" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="p-4 pb-3">
            <div className="position-relative">
              <input
                type="text"
                className="form-control rounded-pill ps-4 pe-5 py-3"
                placeholder="Search..."
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "none",
                  fontSize: "15px",
                }}
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="position-absolute end-0 top-50 translate-middle-y me-4 text-muted"
                size="sm"
              />
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="px-4 py-2">
            <ul className="list-unstyled mb-0">
              <li className="mb-1">
                <Link
                  className="nav-link text-dark py-3 px-3 rounded-3 d-block fw-medium"
                  to="/"
                  onClick={toggleMobileMenu}
                  style={{
                    transition: "all 0.2s ease",
                    fontSize: "16px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f8f9fa";
                    e.target.style.transform = "translateX(8px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.transform = "translateX(0)";
                  }}
                >
                  Home
                </Link>
              </li>
              <li className="mb-1">
                <Link
                  className="nav-link text-dark py-3 px-3 rounded-3 d-block fw-medium"
                  to="/about"
                  onClick={toggleMobileMenu}
                  style={{
                    transition: "all 0.2s ease",
                    fontSize: "16px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f8f9fa";
                    e.target.style.transform = "translateX(8px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.transform = "translateX(0)";
                  }}
                >
                  About
                </Link>
              </li>

              {/* Services Expandable */}
              <li className="mb-1">
                <button
                  className="btn w-100 text-start nav-link text-dark py-3 px-3 rounded-3 d-flex justify-content-between align-items-center fw-medium border-0"
                  onClick={toggleMobileServices}
                  style={{
                    transition: "all 0.2s ease",
                    fontSize: "16px",
                    backgroundColor: mobileServicesOpen
                      ? "#f8f9fa"
                      : "transparent",
                  }}
                >
                  <span>Services</span>
                  <FontAwesomeIcon
                    icon={mobileServicesOpen ? faChevronUp : faChevronDown}
                    className="text-muted"
                    style={{
                      transition: "transform 0.3s ease",
                    }}
                  />
                </button>

                <div
                  style={{
                    maxHeight: mobileServicesOpen ? "300px" : "0",
                    overflow: "hidden",
                    transition:
                      "max-height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                  }}
                >
                  <div className="ps-4 pt-2">
                    {servicesList.map((srv, index) => (
                      <Link
                        key={srv.slug}
                        className="nav-link text-muted py-2 px-3 rounded-2 d-block"
                        to={`/services/${srv.slug}`}
                        onClick={toggleMobileMenu}
                        style={{
                          fontSize: "14px",
                          transition: "all 0.2s ease",
                          opacity: mobileServicesOpen ? 1 : 0,
                          transform: mobileServicesOpen
                            ? "translateY(0)"
                            : "translateY(-10px)",
                          transitionDelay: mobileServicesOpen
                            ? `${index * 0.05}s`
                            : "0s",
                          listStyle: "none",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#e9ecef";
                          e.target.style.color = "#495057";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          e.target.style.color = "#6c757d";
                        }}
                      >
                        • {srv.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </li>

              <li className="mb-1">
                <Link
                  className="nav-link text-dark py-3 px-3 rounded-3 d-block fw-medium"
                  to="/blogs"
                  onClick={toggleMobileMenu}
                  style={{
                    transition: "all 0.2s ease",
                    fontSize: "16px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f8f9fa";
                    e.target.style.transform = "translateX(8px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.transform = "translateX(0)";
                  }}
                >
                  Blogs
                </Link>
              </li>
              <li className="mb-1">
                <Link
                  className="nav-link text-dark py-3 px-3 rounded-3 d-block fw-medium"
                  to="/contact"
                  onClick={toggleMobileMenu}
                  style={{
                    transition: "all 0.2s ease",
                    fontSize: "16px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f8f9fa";
                    e.target.style.transform = "translateX(8px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.transform = "translateX(0)";
                  }}
                >
                  Contact
                </Link>
              </li>
            </ul>
          </nav>

          {/* CTA Button */}
          <div className="p-4 ">
            <Link to="/contact" onClick={toggleMobileMenu}>
              <AnimatedButton text="Get in Touch" className="w-100" />
            </Link>
          </div>

          {/* Social Links */}
          <div className="border-top border-light mt-4 p-4">
            <div className="text-center">
              <p className="text-muted mb-3 small">Follow us on</p>
              <div className="d-flex justify-content-center gap-4">
                <button
                  type="button"
                  className="border-0"
                  onClick={() => window.open("https://facebook.com", "_blank")}
                  style={{
                    width: "44px",
                    height: "44px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#3b5998";
                    e.target.style.transform = "translateY(-3px)";
                    e.target.querySelector("svg").style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#f8f9fa";
                    e.target.style.transform = "translateY(0)";
                    e.target.querySelector("svg").style.color = "#6c757d";
                  }}
                >
                  <FontAwesomeIcon icon={faFacebookF} className="text-muted" />
                </button>
                <button
                  type="button"
                  className="border-0"
                  onClick={() => window.open("https://instagram.com", "_blank")}
                  style={{
                    width: "44px",
                    height: "44px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background =
                      "linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)";
                    e.target.style.transform = "translateY(-3px)";
                    e.target.querySelector("svg").style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#f8f9fa";
                    e.target.style.transform = "translateY(0)";
                    e.target.querySelector("svg").style.color = "#6c757d";
                  }}
                >
                  <FontAwesomeIcon icon={faInstagram} className="text-muted" />
                </button>
                <button
                  type="button"
                  className="border-0"
                  onClick={() => window.open("https://linkedin.com", "_blank")}
                  style={{
                    width: "44px",
                    height: "44px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#0077b5";
                    e.target.style.transform = "translateY(-3px)";
                    e.target.querySelector("svg").style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#f8f9fa";
                    e.target.style.transform = "translateY(0)";
                    e.target.querySelector("svg").style.color = "#6c757d";
                  }}
                >
                  <FontAwesomeIcon icon={faLinkedinIn} className="text-muted" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
